import styled, { DefaultTheme, css } from 'styled-components'

type ContainerProps = {
  inline?: boolean
  grid?: number
}

const containerModifiers = {
  inline: () => css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.5rem;
  `,
  grid: (grid: number) => css`
    display: grid;
    grid-template-columns: ${`repeat(${grid}, 1fr)`};
    gap: 0.5rem;

    .checkboxFieldWrapper {
      --padding: 0.25rem 0.5rem;
    }
  `
}

export const Container = styled.div<ContainerProps>`
  ${({ inline, grid }) => css`
    ${inline && containerModifiers.inline()}
    ${grid && containerModifiers.grid(grid)}
  `}
`

/**
 * Item
 */

type ItemProps = {
  size?: CheckboxItemSizes
  hasActiveChildren?: boolean
  isSelected?: boolean
}

const itemModifiers = {
  isSelected: () => css`
    box-shadow: 0px 4px 8px rgba(24, 21, 60, 0.08);
  `,
  activeChildren: () => css`
    label {
      margin-bottom: 0;
    }
  `,
  size: {
    small: (theme: DefaultTheme) => css`
      --padding: ${theme.spacings.xxxsmall};
    `,
    medium: (theme: DefaultTheme) => css`
      --padding: ${theme.spacings.medium};
    `
  }
}

export const Item = styled.div<ItemProps>`
  ${({ theme, hasActiveChildren, isSelected, size = 'medium' }) => css`
    --borderColor: ${theme.colors.hover};
    --backgroundColor: ${theme.colors.primaryLighten60};
    /* Controlando layers para o activeChildren  */
    position: relative;
    z-index: 2;
    box-shadow: 0;

    border-radius: var(--sl-radius-2);

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;

      * {
        cursor: not-allowed;
      }

      label {
        &:hover {
          color: inherit;
        }
      }
    }

    &.hide,
    input[type='checkbox'] {
      display: none;
    }

    /* Label */
    label {
      position: relative;

      display: flex;
      align-items: flex-start;
      flex: auto;
      flex-wrap: wrap;

      border-radius: var(--sl-radius-2);
      padding: var(--padding);
      height: 100%;

      border: 1px solid ${theme.colors.inative};
      background-color: ${theme.colors.light};

      transition: all ${theme.transition.fast};
      cursor: pointer;

      color: var(--sl-fg-base-soft);

      .labelWrapper {
        color: var(--sl-fg-base);
      }

      .label {
        font-weight: 500;
      }

      &:hover {
        background-color: ${theme.colors.primaryLighten60};
      }

      .checkbox-wrapper {
        display: flex;
        gap: 0.5rem;
        align-items: flex-start;
      }

      .checkbox-icon {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        width: var(--bulletWrapperSize);
        min-width: var(--bulletWrapperSize);
        height: var(--bulletWrapperSize);
        border-radius: ${theme.border.radius.medium};
        border: var(--sl-border-base);
        background-color: ${theme.colors.white};

        &:focus-visible {
          box-shadow: var(--sl-focus-ring-base);
          border: var(--sl-border-base-strong-hover);
        }

        &:hover:not(.disabled) {
          border: var(--sl-border-base-strong-hover);
        }
      }

      .icon-check {
        display: none;
        color: ${theme.colors.white};
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    }

    /* Input */
    input[type='checkbox']:checked,
    &.selected input[type='checkbox'] {
      /* The .selected class is used in the checkbox list */
      + label {
        border-color: var(--borderColor);
        background-color: var(--backgroundColor);
      }

      + label .checkbox-icon {
        background-color: ${theme.colors.primary};
        border-color: ${theme.colors.primary};

        .icon-check {
          display: block;
        }

        &:focus-visible {
          background-color: color-mix(in srgb, var(--primary) 80%, black);
          box-shadow:
            0rem 0rem 0rem 0.0625rem var(--sl-color-gray-0),
            0rem 0rem 0rem 0.1875rem
              color-mix(in srgb, var(--primary) 40%, transparent);
        }

        &:hover:not(.disabled) {
          background-color: color-mix(in srgb, var(--primary) 80%, black);
          border: color-mix(in srgb, var(--primary) 80%, black);
        }
      }
    }

    ${hasActiveChildren && isSelected && itemModifiers.activeChildren()}
    ${isSelected && itemModifiers.isSelected()}
    ${itemModifiers.size[size](theme)}
  `}
`

export const LabelWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    width: 100%;

    color: ${theme.colors.dark};
  `}
`

export const Icon = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    margin-right: 0.25rem;

    font-size: ${theme.font.sizes.small};
  `}
`

export const LabelText = styled.div`
  display: flex;
  align-items: center;
`

export const Description = styled.div`
  ${({ theme }) => css`
    flex: 1 1 100%;
    line-height: 1.125;

    font-size: ${theme.font.sizes.small};
  `}
`

export const ActiveChildren = styled.div`
  ${({ theme }) => css`
    display: flex;
    margin-top: -${theme.spacings.medium};
    margin-bottom: 0.5rem; // Retornando MB default do label
    z-index: 1;

    border-radius: ${theme.border.radius.medium};
    padding: 2rem ${theme.spacings.medium} ${theme.spacings.medium}
      ${theme.spacings.medium};

    border: 1px solid ${theme.colors.tertiary};
    background-color: ${theme.colors.white};

    transition: all ${theme.transition.fast};
  `}
`
